'use client';
import React, { useEffect } from 'react';
import styled from '@benzinga/themetron';
import { Sparkline } from '@benzinga/core-ui';
import Hooks from '@benzinga/hooks';
import { getClassnameByValue } from '@benzinga/frontend-utils';
import { CryptoDotComCoinResponse, CoinInput } from '../entities';
import {
  formatPrice,
  formatPricesToChartPoints,
  getChartColorFromPrices,
  formatPercentage,
  CryptoDotComLink,
  getCoinIcon,
  getCoinsData,
} from '../utils';

export interface CoinListProps {
  coins: CoinInput[];
}

export const CoinList: React.FC<CoinListProps> = ({ coins }) => {
  const [data, setData] = React.useState<CryptoDotComCoinResponse[] | CoinInput[]>(coins);

  useEffect(() => {
    getCoinsData(coins).then(data => setData(data));
  }, [coins]);

  const [ref, { width }] = Hooks.useElementSize();
  const compact = width <= 360;

  return (
    <CoinListContainer className="coin-list-container" ref={ref}>
      <ul className="coin-list">
        {data.map(coin => (
          <li key={coin?.slug}>
            <a
              data-action="Crypto.com Coin List Item Click"
              data-category="Outbound Links"
              href={`https://crypto.com/price/${coin?.slug}?utm_medium=widget&amp;utm_campaign=CoinList&amp;utm_source=www.benzinga.com&amp;utm_id=${coin.slug}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <div className="coin-info-left-section">
                <img alt={coin.name} className="coin-image" src={getCoinIcon(coin?.slug)} />
                <div className="coin-name-symbol-container">
                  <span className="coin-name">{coin.name}</span>
                  <span className="coin-symbol">{coin.symbol}</span>
                </div>
              </div>
              {!compact && coin.prices && (
                <div className="coin-info-middle-section">
                  <Sparkline
                    color={getChartColorFromPrices(coin.prices)}
                    data={formatPricesToChartPoints(coin.prices)}
                    height={30}
                    width={180}
                  />
                </div>
              )}
              <div className="coin-info-right-section">
                <span className="coin-price">${coin.usd_price ? formatPrice(coin.usd_price) : '–'}</span>
                <span
                  className={`coin-price-change ${getClassnameByValue(coin.usd_price_change_24h)}`}
                  title={formatPercentage(coin.usd_price_change_24h)}
                >
                  {coin.usd_price_change_24h ? formatPercentage(coin.usd_price_change_24h) : '–'}
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
      <div className="coin-list-footer">
        <CryptoDotComLink />
      </div>
    </CoinListContainer>
  );
};

const CoinListContainer = styled.div`
  &.coin-list-container {
    width: 100%;
    font-family:
      BlinkMacSystemFont,
      -apple-system,
      Segoe UI,
      Roboto,
      Oxygen,
      Ubuntu,
      Cantarell,
      Fira Sans,
      Droid Sans,
      Helvetica Neue,
      Helvetica,
      Arial,
      sans-serif;
    font-weight: 500;
    color: #000000;

    .coin-list {
      width: 100%;

      li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        color: #000000;
        font-size: 12px;
        border: 1px solid rgba(119, 119, 119, 0.19);
        border-top: none;
        margin-top: 1px;
        width: 100%;
        height: 54px;
        padding: 0.5rem 1rem;
        border-radius: ${({ theme }) => theme.borderRadius.sm};
        box-sizing: border-box;

        &:first-of-type {
          border-top: 1px solid rgba(119, 119, 119, 0.19);
        }

        a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }
      }

      .coin-info-left-section {
        display: flex;

        > img {
          margin-right: 0.5rem;
        }
      }

      .coin-info-right-section {
        display: flex;
        flex-direction: column;
      }

      .coin-name-symbol-container {
        display: flex;
        flex-direction: column;

        .coin-name {
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #000000;
        }

        .coin-symbol {
          color: rgb(123, 132, 155);
          text-transform: uppercase;
          text-align: left;
        }
      }

      .coin-image {
        width: 24px;
        height: 24px;
      }

      .coin-price {
        text-align: right;
        color: #000000;
      }

      .coin-price-change {
        text-align: right;
        font-weight: 700;

        &.up {
          color: #20bca4;
        }

        &.down {
          color: #e64b60;
        }
      }
    }

    .coin-list-footer {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      width: 100%;

      .view-more-now-link {
        transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
        transition-duration: 150ms;
        transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
        cursor: pointer;
        text-decoration: none;
        outline: transparent solid 2px;
        outline-offset: 2px;
        color: rgb(17, 153, 250);
        display: flex;
        -webkit-box-pack: end;
        justify-content: flex-end;
        height: 32px;
        bottom: calc(1rem * -1);

        > div {
          display: flex;
          -webkit-box-align: center;
          align-items: center;
          -webkit-box-pack: center;
          justify-content: center;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 600;
          width: 138px;
          height: 100%;
          background: rgb(17, 153, 250);
          color: #ffffff;
          cursor: pointer;
        }
      }
    }
  }
`;
